import { useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const AVATAR_COLOR_CLASSES = [
  "bg-primary-subtle text-primary",
  "bg-success-subtle text-success",
  "bg-danger-subtle text-danger",
  "bg-warning-subtle text-warning",
  "bg-info-subtle text-info",
  "bg-secondary-subtle text-secondary",
];

export const getAvatarColorClass = (str: string = "") => {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % AVATAR_COLOR_CLASSES.length;

  return AVATAR_COLOR_CLASSES[index];
};

const AVATAR_SIZE_CLASSES = {
  xs: "avatar-xs",
  sm: "avatar-sm",
  lg: "avatar-lg",
  xl: "avatar-xl",
} as const;

type AvatarSize = keyof typeof AVATAR_SIZE_CLASSES;

const Avatar = ({
  name = "",
  image,
  className = "",
  lightbox = false,
  size,
}: {
  name?: string;
  image?: string | null;
  className?: string;
  lightbox?: boolean;
  /** Wraps the avatar in a sized container (e.g. "avatar-sm") — omit to fill the parent instead. */
  size?: AvatarSize;
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const showImage = image && !imageFailed;

  const initials = name
    .trim()
    .split(" ")
    .filter((p) => p.trim().length > 0)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  const colorClass = getAvatarColorClass(name);

  const content = showImage ? (
    <>
      <img
        src={image!}
        alt={name || "Avatar"}
        className={`rounded-circle  object-fit-cover w-100 h-100 d-block ${lightbox ? "cursor-pointer" : ""} ${className}`}
        onError={() => setImageFailed(true)}
        onClick={lightbox ? () => setLightboxOpen(true) : undefined}
      />
      {lightbox && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={[{ src: image! }]}
          styles={{
            root: { "--yarl__color_backdrop": "rgba(0, 0, 0, 0.5)" },
          }}
          render={{ buttonPrev: () => null, buttonNext: () => null }}
        />
      )}
    </>
  ) : (
    <div
      className={`avatar-title rounded-circle w-100 h-100 d-flex align-items-center justify-content-center ${colorClass}`}
    >
      {initials}
    </div>
  );

  if (size) {
    return (
      <div className={`flex-shrink-0 ${AVATAR_SIZE_CLASSES[size]}`}>
        {content}
      </div>
    );
  }

  return content;
};

export default Avatar;
